if (!customElements.get('product-landing-story')) {
  customElements.define(
    'product-landing-story',
    class ProductLandingStory extends HTMLElement {
      connectedCallback() {
        this.finishChoices = Array.from(this.querySelectorAll('[data-finish-choice]'));
        this.storyImage = this.querySelector('[data-story-image]');
        this.storyVariantMedia = this.parseStoryVariantMedia();
        this.productInfo = document.querySelector(
          `.product-info--main[data-product-id="${this.dataset.productId}"]`
        );
        this.productSectionId = this.productInfo?.dataset.section;

        this.finishChoices.forEach((choice) => {
          choice.addEventListener('click', () => this.selectFinish(choice));
        });

        this.onVariantLoading = (event) => this.handleVariantLoading(event);
        this.productInfo?.addEventListener('product-info:variant-loading', this.onVariantLoading);
        this.syncFinishAvailability(this.productInfo);

        if (
          this.productSectionId &&
          this.dataset.finishPosition !== '' &&
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined'
        ) {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event?.data?.sectionId !== this.productSectionId) return;

            const position = Number(this.dataset.finishPosition);
            const finishName = event.data.variant?.options?.[position];
            if (finishName) this.markSelectedFinish(finishName);
            this.updateStoryImage(event.data.variant);
            this.clearPendingFinish();
            this.syncFinishAvailability(event.data.html);
          });
        }
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        this.productInfo?.removeEventListener('product-info:variant-loading', this.onVariantLoading);
      }

      parseStoryVariantMedia() {
        const source = this.querySelector('[data-story-variant-media]');
        if (!source) return {};

        try {
          return JSON.parse(source.textContent);
        } catch (error) {
          return {};
        }
      }

      updateStoryImage(variant) {
        if (!this.storyImage || !variant?.id) return;

        const media = this.storyVariantMedia[String(variant.id)];
        if (!media?.src || this.storyImage.dataset.storyVariantId === String(variant.id)) return;

        this.storyImage.classList.add('is-changing');
        const preload = new Image();

        preload.addEventListener(
          'load',
          () => {
            this.storyImage.removeAttribute('srcset');
            this.storyImage.src = media.src;
            this.storyImage.alt = media.alt || this.storyImage.alt;
            this.storyImage.dataset.storyVariantId = String(variant.id);
            requestAnimationFrame(() => this.storyImage.classList.remove('is-changing'));
          },
          { once: true }
        );
        preload.addEventListener('error', () => this.storyImage.classList.remove('is-changing'), { once: true });
        preload.src = media.src;
      }

      selectFinish(choice) {
        if (!this.productInfo || choice.disabled) return;

        const optionValueId = choice.dataset.optionValueId;
        const pickerTarget = Array.from(
          this.productInfo.querySelectorAll('variant-selects [data-option-value-id]')
        ).find((target) => target.dataset.optionValueId === optionValueId);

        if (!pickerTarget || pickerTarget.disabled || pickerTarget.classList.contains('disabled')) return;

        this.pendingFinishChoice = choice;
        choice.classList.add('is-pending');
        this.setAttribute('data-finish-loading', '');
        const status = choice.querySelector('[data-finish-status]');
        if (status) status.textContent = this.dataset.updatingCopy || 'Updating finish';

        if (pickerTarget.tagName === 'OPTION') {
          const select = pickerTarget.closest('select');
          if (!select) return;
          select.value = pickerTarget.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          pickerTarget.click();
        }
      }

      handleVariantLoading(event) {
        if (event.detail?.sectionId !== this.productSectionId) return;
        if (event.detail.isLoading) {
          this.setAttribute('data-finish-loading', '');
        } else {
          this.clearPendingFinish();
        }
      }

      clearPendingFinish() {
        this.removeAttribute('data-finish-loading');
        this.finishChoices.forEach((choice) => {
          choice.classList.remove('is-pending');
          const status = choice.querySelector('[data-finish-status]');
          if (!status) return;
          if (choice.disabled) {
            status.textContent = this.dataset.unavailableCopy || 'Unavailable for this selection';
          } else {
            status.textContent = status.dataset.availableCopy;
          }
        });
        this.pendingFinishChoice = null;
      }

      markSelectedFinish(finishName) {
        const normalizedFinish = String(finishName).trim().toLowerCase();

        this.finishChoices.forEach((choice) => {
          const selected = String(choice.dataset.finishName).trim().toLowerCase() === normalizedFinish;
          choice.classList.toggle('is-selected', selected);
          choice.setAttribute('aria-pressed', String(selected));
        });
      }

      syncFinishAvailability(source) {
        if (!source) return;

        const variantSelects = Array.from(source.querySelectorAll('variant-selects')).find(
          (element) => element.id === `variant-selects-${this.productSectionId}`
        );
        if (!variantSelects) return;

        const pickerTargets = Array.from(variantSelects.querySelectorAll('[data-option-value-id]'));
        this.finishChoices.forEach((choice) => {
          const target = pickerTargets.find(
            (pickerTarget) => pickerTarget.dataset.optionValueId === choice.dataset.optionValueId
          );
          if (!target) return;

          const unavailable = target.disabled || target.classList.contains('disabled');
          const status = choice.querySelector('[data-finish-status]');
          choice.disabled = unavailable;
          choice.classList.toggle('is-unavailable', unavailable);
          choice.setAttribute('aria-disabled', String(unavailable));
          if (status) {
            status.textContent = unavailable
              ? this.dataset.unavailableCopy || 'Unavailable for this selection'
              : status.dataset.availableCopy;
          }
        });
      }
    }
  );
}
